import {
  CommandId,
  DXR,
  FieldApi,
  HyperlinkClickEventArgs,
  IntervalApi,
  RichEdit,
  Table,
} from 'components/devexpress/RichEdit/dx-richedit';
import { RibbonSelectBoxItem } from 'devexpress-richedit';
import { RichEditDocumentClientApi } from 'devexpress-richedit/lib/client/api/document';
import { SubDocumentClientApi } from 'devexpress-richedit/lib/client/api/sub-document';
import { ParagraphCollection } from 'devexpress-richedit/lib/client/model-api/collections/paragraph-collection';
import { ParagraphStyle } from 'devexpress-richedit/lib/common/model/paragraph/paragraph-style';

export type DocumentVariable = {
  id: number;
  postfix: string | null;
};

export const checkBoxTooltipKey = 'CheckBox';
export const checkBoxUncheckedSymbol = 'U';
export const checkBoxCheckedSymbol = 'C';
export const checkAltCode = '\u2611';
export const uncheckAltCode = '\u2610';

// base RichEdit document has 8 paragraph styles
const baseParagraphStylesCount = 8;
const paragraphStylesItemIndex = 20;

export type ColorTarget = 'font' | 'highlight' | 'cell' | 'border';

/**
 * Uses hyperlink to insert checkbox in document.
 */
export function insertCheckBox(s: RichEdit) {
  var subDocument = s.selection.activeSubDocument;
  s.beginUpdate();
  s.history.beginTransaction();
  var hyperlink = subDocument.hyperlinks.create(
    s.selection.active,
    new DXR.HyperlinkInfo(
      '',
      checkBoxUncheckedSymbol,
      undefined,
      checkBoxTooltipKey,
    ),
  );
  subDocument.deleteText(hyperlink.resultInterval);
  subDocument.insertText(hyperlink.resultInterval.start, uncheckAltCode);
  var insertedInterval = subDocument.insertText(hyperlink.interval.end, ' ');
  s.selection.setSelection(insertedInterval.end);
  s.history.endTransaction();
  s.endUpdate();
}

/**
 * Determines if hyperlink is checkbox and changes state if yes.
 */
export function onHyperlinkClick(s: RichEdit, e: HyperlinkClickEventArgs) {
  if (
    e.targetUri == checkBoxUncheckedSymbol ||
    e.targetUri == checkBoxCheckedSymbol
  ) {
    var subDocument = e.hyperlink.subDocument;
    var checked = e.targetUri === checkBoxCheckedSymbol;
    s.beginUpdate();
    s.history.beginTransaction();
    var hypInfo = e.hyperlink.hyperlinkInfo;
    hypInfo.url = checked ? checkBoxUncheckedSymbol : checkBoxCheckedSymbol;
    hypInfo.tooltip = checkBoxTooltipKey;
    e.hyperlink.hyperlinkInfo = hypInfo;
    subDocument.deleteText(e.hyperlink.resultInterval);
    subDocument.insertText(
      e.hyperlink.resultInterval.start,
      checked ? uncheckAltCode : checkAltCode,
    );
    s.selection.setSelection(e.hyperlink.interval.end);
    s.history.endTransaction();
    s.endUpdate();
    e.handled = true;
  }
}

/**
 * Uses ribbon tabs commands to apply color depending on target.
 * */
export function applyColor(
  richEdit: RichEdit,
  color: string,
  target: ColorTarget,
) {
  switch (target) {
    case 'font':
      richEdit.executeCommand(
        DXR.HomeTabItemId.ChangeFontForeColor as unknown as CommandId,
        color,
      );
      break;
    case 'highlight':
      richEdit.executeCommand(
        DXR.HomeTabItemId.ChangeParagraphBackColor as unknown as CommandId,
        color,
      );
      break;
    case 'cell':
      richEdit.executeCommand(
        DXR.TableDesignTabItemId.ChangeTableCellShading as unknown as CommandId,
        color,
      );
      break;
    case 'border':
      richEdit.executeCommand(
        DXR.TableDesignTabItemId
          .ChangeTableBorderColorRepositoryItem as unknown as CommandId,
        color,
      );
      break;
  }

  richEdit.focus();
}

// Highlights interval using RangePermissions. If interval contain a table, each cell is highlighted separately
export function highlightInterval(richEdit: RichEdit, interval: IntervalApi) {
  if (!richEdit || !interval) return;

  const tables = richEdit.document.tables.find(interval);
  if (tables.length != 0) {
    removeHighlighting(richEdit, interval);
    for (const tableApi of tables) {
      const table = (tableApi as any)._table as Table;
      const fields = richEdit.document.fields.find(table.interval);
      for (const field of fields) {
        for (const row of table.rows) {
          for (const cell of row.cells) {
            if (
              cell.interval.start >= field.resultInterval.start &&
              cell.interval.end <= field.resultInterval.end
            )
              highlightIntervalWithoutTableChecks(
                richEdit,
                cell.interval,
                false,
              );
            else if (
              cell.interval.start <= field.resultInterval.start &&
              cell.interval.end >= field.resultInterval.end
            )
              highlightIntervalWithoutTableChecks(
                richEdit,
                field.resultInterval,
                true,
              );
          }
        }
      }
    }
  } else {
    highlightIntervalWithoutTableChecks(richEdit, interval);
  }
}

function removeHighlighting(richEdit: RichEdit, interval: IntervalApi) {
  richEdit.document.rangePermissions
    .find({
      position: interval,
    })
    .forEach((r) => r.delete());
}

export function highlightIntervalWithoutTableChecks(
  richEdit: RichEdit,
  interval: IntervalApi,
  incrementIntervalLength: boolean = true,
) {
  if (incrementIntervalLength) interval.length++;
  removeHighlighting(richEdit, interval);
  richEdit.document.rangePermissions.create(interval);
}

/*
 * Fixes disappeared text highlight
 * */
export function updateRangePermissionHighlight(richEdit: RichEdit) {
  // Highlight state changing is needed, because without it only last ranges are highlighted
  richEdit.rangePermissionOptions.highlightRanges = false;
  richEdit.rangePermissionOptions.highlightRanges = true;
  richEdit.rangePermissionOptions.showBrackets = false;
}

export function updateParagraphStyles(richEdit: RichEdit) {
  // Get whole document styles
  const availableStyles =
    // @ts-ignore
    richEdit.document._core.modelManager.model.paragraphStyles;

  richEdit.updateRibbon((ribbon) => {
    const textStylesTab = ribbon.getTab(DXR.RibbonTabType.Home);
    const paragraphStylesItem = textStylesTab?.items[
      paragraphStylesItemIndex
    ] as RibbonSelectBoxItem | undefined;

    if (!paragraphStylesItem?.dataSource) return;

    const usedStyleNames = getUsedParagraphStyles(richEdit.document.paragraphs);
    if (!usedStyleNames) return;

    removeNotDefaultAndUnusedStyles(
      paragraphStylesItem.dataSource,
      usedStyleNames,
    );

    addMissingStyles(
      paragraphStylesItem.dataSource,
      usedStyleNames,
      availableStyles,
    );
  });
}

function areIntervalsIntersect(interval1: IntervalApi, interval2: IntervalApi) {
  // ..2S..1S..1E..2E..
  if (interval1.start >= interval2.start && interval1.end <= interval2.end)
    return true;
  // ..1S..2S..1E..2E..
  if (
    interval1.start <= interval2.start &&
    interval1.end >= interval2.start &&
    interval1.end <= interval2.end
  )
    return true;
  // ..2S..1S..2E..1E..
  if (
    interval1.start >= interval2.start &&
    interval1.start <= interval2.end &&
    interval1.end >= interval2.end
  )
    return true;
  // ..1S..2S..2E..1E..
  if (interval1.start <= interval2.start && interval1.end >= interval2.end)
    return true;

  return false;
}

export function isIntervalInsideAnother(
  child: IntervalApi,
  parent: IntervalApi,
) {
  return child.start >= parent.start && child.end <= parent.end;
}

/**
 * Checks if selection is empty or contains field, image or table
 */
export function isSelectionInvalidForRephrasing(richEdit: RichEdit) {
  if (richEdit.readOnly) return true;
  const selection = richEdit.selection;

  if (selection.end - selection.start <= 0) {
    return true;
  }

  if (
    selection.intervals.every(
      (interval) => richEdit.document.getText(interval).trim().length === 0,
    )
  ) {
    return true;
  }

  let amountOfSelectedCells = 0;
  for (const selectionInterval of selection.intervals) {
    // Check selection for tables.
    // If selection contains only a single cell from a table or if it doesn't contain any cells at all, check will pass.
    const tables = richEdit.document.tables.find(selectionInterval);
    if (tables.length > 1) return true;
    if (tables.length == 1) {
      const table = tables[0];
      const cellsIntervals = getTableCellsIntervals(
        (table as any)._table as Table,
      );

      // It is needed, because selection of cell includes positions of last symbol of previous cell and first symbol of next cell
      selectionInterval.start++;
      selectionInterval.length -= 2;

      amountOfSelectedCells += cellsIntervals.filter((cellInterval) =>
        areIntervalsIntersect(cellInterval, selectionInterval),
      ).length;

      if (amountOfSelectedCells != 1) {
        return true;
      }
    }

    // Check selection for fields.
    const fields = richEdit.document.fields.find(selectionInterval);
    if (fields.length > 0) return true;

    // Check selection for images.
    const images = richEdit.document.images.find(selectionInterval);
    if (images.length > 0) return true;
  }

  return false;
}

export const isSelectionValidForDescriptionGeneration = (
  richEdit: RichEdit,
): boolean => {
  if (richEdit.readOnly) return false;

  const selection = richEdit.selection.intervals[0];
  const variableFields = richEdit.document.fields
    .find(selection)
    .filter((f) => extractDocVariableId(richEdit.document, f) !== undefined);

  return variableFields.length !== 0;
};

/*Find in document field that is table variable by passed 'variableId'*/
export function findTableVariableFieldById(
  document: RichEditDocumentClientApi,
  variableId: number,
) {
  const tables = document.tables;
  for (let i = 0; i < tables.count; i++) {
    const table = tables.getByIndex(i);
    if (table) {
      const fields = document.fields.find(table.interval);
      const varField = fields.find((field) =>
        document
          .getText(field.codeInterval)
          .includes(`DOCVARIABLE ${variableId}`),
      );
      if (varField) return varField;
    }
  }
}

export function getDocumentVariablesIds(document: RichEditDocumentClientApi) {
  const fields = document.fields;
  const result: DocumentVariable[] = [];

  for (let i = 0; i < fields.count; i++) {
    const field = fields.getByIndex(i);
    if (!field) continue;

    const codeText = document.getText(field.codeInterval);
    const docVariable = parseDocVariable(codeText);

    if (docVariable) {
      result.push(docVariable);
    }
  }

  return result;
}

/**
 * Executes an asynchronous function while showing a loading panel.
 *
 * The loading panel is shown before the function starts and hiding
 * after the function finishes or throws an error.
 *
 * @template T The result type of the asynchronous function.
 * @param richEdit The RichEdit instance.
 * @param fn An asynchronous function to be executed. The return value of this function
 * will also be returned from `withLoadingPanel`.
 */
export async function withLoadingPanel<T>(
  richEdit: RichEdit,
  fn: () => Promise<T>,
) {
  richEdit.loadingPanel.show();
  try {
    return await fn();
  } finally {
    richEdit.loadingPanel.hide();
  }
}

function getTableCellsIntervals(table: Table) {
  let cellsIntervals: IntervalApi[] = [];
  const rows = table.rows;
  for (const row of rows) {
    for (const cell of row.cells) {
      cellsIntervals.push(cell.interval);
    }
  }

  return cellsIntervals;
}

// Replaces css functions that are not/partially supported by DevExpress from svg
export function normalizeSvg(svg: string) {
  // Replaces light-dark(cssColor1, cssColor2) with cssColor1
  function resolveLightDark(inputString: string) {
    const funcName = 'light-dark';
    let result = inputString;

    while (true) {
      const startIndex = result.indexOf(`${funcName}(`);

      if (startIndex === -1) break;

      let pointer = startIndex + funcName.length + 1;
      let parenthesisBalance = 1;
      let commaIndex = -1;
      let endIndex = -1;

      for (let i = pointer; i < result.length; i++) {
        const char = result[i];

        if (char === '(') {
          parenthesisBalance++;
        } else if (char === ')') {
          parenthesisBalance--;
        } else if (
          char === ',' &&
          parenthesisBalance === 1 &&
          commaIndex === -1
        ) {
          commaIndex = i;
        }

        if (parenthesisBalance === 0) {
          endIndex = i;
          break;
        }
      }

      if (commaIndex !== -1 && endIndex !== -1) {
        const firstArg = result
          .substring(startIndex + funcName.length + 1, commaIndex)
          .trim();
        result =
          result.substring(0, startIndex) +
          firstArg +
          result.substring(endIndex + 1);
      } else {
        break;
      }
    }

    return result;
  }

  return resolveLightDark(
    svg.replace(/<foreignObject[\s\S]*?<\/foreignObject>/g, ''),
  );
}

export const extractDocVariableId = (
  document: RichEditDocumentClientApi | SubDocumentClientApi,
  variableField: FieldApi,
) => {
  const codeText = document.getText(variableField.codeInterval);
  const parsed = parseDocVariable(codeText);

  return parsed?.id;
};

export const extractDocVariableIdAndPostfix = (
  document: RichEditDocumentClientApi,
  variableField: FieldApi,
) => {
  const codeText = document.getText(variableField.codeInterval);
  return parseDocVariable(codeText);
};

function parseDocVariable(codeText: string): DocumentVariable | null {
  if (!codeText.includes('DOCVARIABLE')) return null;

  const varIdString = codeText.substring('DOCVARIABLE'.length);
  const [idString, postfix = ''] = varIdString.split(':', 2);
  const id = parseInt(idString, 10);

  if (isNaN(id)) return null;

  return { id, postfix };
}

/**
 * Returns paragraph styles that are used in the document.
 * @param paragraphs Document paragraphs.
 */
function getUsedParagraphStyles(paragraphs: ParagraphCollection) {
  let usedStyleNames: string[] = [];
  for (let i = 0; i < paragraphs.count; i++) {
    const paragraph = paragraphs.getByIndex(i);
    if (!paragraph) return;
    // @ts-ignore
    const paragraphStyleName = paragraph._paragraph.paragraphStyle.styleName;
    if (!usedStyleNames.includes(paragraphStyleName)) {
      usedStyleNames.push(paragraphStyleName);
    }
  }

  return usedStyleNames;
}

type RibbonSelectBoxDataSource = { text: string; value: string }[];

/**
 * Removes styles from ribbon that aren't RichEdit default styles and aren't used in the current document.
 * @param displayedStyles Paragraph styles that are currently in ribbon select item.
 * @param usedStyles Paragraph styles that are used in document.
 * @remarks This may be necessary when a document with its styles was previously imported,
 * and then a new document with new styles is imported.
 */
function removeNotDefaultAndUnusedStyles(
  displayedStyles: RibbonSelectBoxDataSource,
  usedStyles: string[],
) {
  for (
    let displayedStyleIndex = 0;
    displayedStyleIndex < displayedStyles.length;
    displayedStyleIndex++
  ) {
    const displayedStyle = displayedStyles[displayedStyleIndex];
    if (
      !usedStyles.includes(`¶${displayedStyle.text}`) &&
      displayedStyleIndex > baseParagraphStylesCount
    ) {
      displayedStyles.splice(displayedStyleIndex, 1);
    }
  }
}

/**
 * Adds new styles from document that are used in document to ribbon.
 * @param displayedStyles Paragraph styles that are currently in the ribbon select item.
 * @param usedStyles Paragraph styles that are used in the document.
 * @param documentStyles All paragraph styles in the document.
 */
function addMissingStyles(
  displayedStyles: RibbonSelectBoxDataSource,
  usedStyles: string[],
  documentStyles: ParagraphStyle[],
) {
  for (const availableStyle of documentStyles) {
    const styleName = availableStyle.styleName;
    if (
      usedStyles.includes(styleName) &&
      displayedStyles.every((s) => !s.value.includes(styleName))
    ) {
      displayedStyles.push({
        text: `¶${styleName}`,
        value: `¶${styleName}`,
      });
    }
  }
}
